import { useState, useEffect, useRef, useMemo } from 'react'
import { resolveTenantFromHostname, type TenantDomainResolution } from '@/utils/domain'
import { applyBrandingCssVariables, removeBrandingCssVariables } from '@/hooks/useBranding'
import { updateFavicon, updateDocumentTitle, resetFaviconAndTitle } from '@/utils/favicon'
import { brandingService } from '@/services/branding.service'
import type { CompanyBranding } from '@/types/branding.types'

export interface UseDomainBrandingReturn {
  resolution: TenantDomainResolution
  branding: CompanyBranding | null
  loading: boolean
  error: string | null
  isTenantResolved: boolean
}

/**
 * Hook de resolução de identidade visual (White-Label) pré-autenticação baseada em hostname / subdomínio.
 */
export function useDomainBranding(
  customHostname?: string,
  customSearchParams?: URLSearchParams | string
): UseDomainBrandingReturn {
  const resolution = useMemo(
    () => resolveTenantFromHostname(customHostname, customSearchParams),
    [customHostname, customSearchParams]
  )

  const [branding, setBranding] = useState<CompanyBranding | null>(() => {
    if (typeof window === 'undefined') return null
    if (resolution.isTenantSubdomain && resolution.slug) {
      try {
        const cached = sessionStorage.getItem(`branding_public_${resolution.slug}`)
        if (cached) {
          const parsed = JSON.parse(cached) as CompanyBranding
          applyBrandingCssVariables(parsed)
          updateFavicon(parsed.logo_icon_url)
          updateDocumentTitle(parsed.brand_name, 'Portal Financeiro')
          return parsed
        }
      } catch {
        // Ignora erro de parse de cache
      }
    } else if (resolution.isCustomDomain && resolution.customDomain) {
      try {
        const cached = sessionStorage.getItem(`branding_public_domain_${resolution.customDomain}`)
        if (cached) {
          const parsed = JSON.parse(cached) as CompanyBranding
          applyBrandingCssVariables(parsed)
          updateFavicon(parsed.logo_icon_url)
          updateDocumentTitle(parsed.brand_name, 'Portal Financeiro')
          return parsed
        }
      } catch {
        // Ignora erro de parse de cache
      }
    }
    return null
  })

  const [loading, setLoading] = useState<boolean>(() => {
    if (resolution.isMainDomain) return false
    if (typeof window !== 'undefined') {
      if (
        resolution.isTenantSubdomain &&
        resolution.slug &&
        sessionStorage.getItem(`branding_public_${resolution.slug}`)
      ) {
        return false
      }
      if (
        resolution.isCustomDomain &&
        resolution.customDomain &&
        sessionStorage.getItem(`branding_public_domain_${resolution.customDomain}`)
      ) {
        return false
      }
    }
    return true
  })

  const [error, setError] = useState<string | null>(null)
  const brandingRef = useRef<CompanyBranding | null>(branding)
  brandingRef.current = branding

  useEffect(() => {
    let isMounted = true

    if (resolution.isMainDomain) {
      setBranding(null)
      setLoading(false)
      setError(null)
      removeBrandingCssVariables()
      resetFaviconAndTitle()
      return
    }

    async function fetchTenantBranding() {
      try {
        let data: CompanyBranding | null = null
        if (resolution.isTenantSubdomain && resolution.slug) {
          data = await brandingService.getPublicBySlug(resolution.slug)
          if (typeof window !== 'undefined' && data) {
            sessionStorage.setItem(`branding_public_${resolution.slug}`, JSON.stringify(data))
          }
        } else if (resolution.isCustomDomain && resolution.customDomain) {
          data = await brandingService.getPublicByDomain(resolution.customDomain)
          if (typeof window !== 'undefined' && data) {
            sessionStorage.setItem(
              `branding_public_domain_${resolution.customDomain}`,
              JSON.stringify(data)
            )
          }
        }

        if (isMounted) {
          setBranding(data)
          setError(null)
          if (data) {
            applyBrandingCssVariables(data)
            updateFavicon(data.logo_icon_url)
            updateDocumentTitle(data.brand_name, 'Portal Financeiro')
          }
        }
      } catch (err: any) {
        if (isMounted) {
          setBranding(null)
          removeBrandingCssVariables()
          resetFaviconAndTitle()
          setError(err?.message || 'Organização não encontrada para o endereço informado.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    void fetchTenantBranding()

    const targetNode = typeof document !== 'undefined' ? document.documentElement : null
    let observer: MutationObserver | null = null
    if (targetNode) {
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            if (brandingRef.current) {
              applyBrandingCssVariables(brandingRef.current)
            }
          }
        }
      })
      observer.observe(targetNode, { attributes: true, attributeFilter: ['data-theme'] })
    }

    return () => {
      isMounted = false
      if (observer) observer.disconnect()
    }
  }, [resolution])

  return {
    resolution,
    branding,
    loading,
    error,
    isTenantResolved: !resolution.isMainDomain && Boolean(branding),
  }
}
